import './CommentBubble.scss';

/**
 * Compact comment chip used as decoration in marketing-page visuals
 * (FeatureTouts row demos). The "floating" mode hangs off a screenshot
 * mock with a caret pointing at the highlighted text.
 *
 * **CommentBubble vs. CommentCard** — these are sibling components,
 * not duplicates:
 *
 *  - `CommentBubble` (this file) is a passive 1-line chip with no
 *    reactions / replies / inputs. It exists so the marketing-page
 *    demo windows can show "here's a comment landing on a feature
 *    surface" without rendering the full thread.
 *  - `CommentCard` (`components/comment/CommentCard.tsx`) is the
 *    full interactive popover with header / body / reactions /
 *    reply list / reply input. Used in the hero Authentication
 *    demo and parallels the production widget's `CommentTooltip`.
 *
 * Same visual language (dark translucent panel, gradient avatar,
 * cyan accent) but different scope. Keep both — collapsing them
 * forces one of the two use sites into a poorly-fitting variant.
 *
 * Style choices that make it feel "alive but quiet":
 *   - dark translucent panel with a hairline stroke (not a hard
 *     border) so it floats over the gradient bg
 *   - colored avatar dot in a fixed slot so multiple bubbles read as
 *     "different commenters" at a glance
 *   - body text capped at ~3 lines to keep the silhouette compact
 *   - subtle backdrop-blur so it survives over busy bg gradient
 */

export interface CommentBubbleProps {
	author: string;
	body: string;
	/** Avatar hue. `random` picks one from a small palette by hashing
	 *  the author name — keeps the same author the same colour across
	 *  re-renders without exporting a palette map. */
	hue?: 'cyan' | 'violet' | 'amber' | 'rose' | 'teal' | 'random';
	/** Render-time positioning helpers — only used for `floating` mode. */
	style?: { top?: string; left?: string; right?: string; bottom?: string };
	/** `floating` (default) = absolute, with caret. `inline` = in flow. */
	variant?: 'floating' | 'inline';
	/** Optional timestamp shown faintly to the right of the author. */
	timestamp?: string;
}

const HUE_PALETTE = ['cyan', 'violet', 'amber', 'rose', 'teal'] as const;

export function CommentBubble(props: CommentBubbleProps) {
	const {
		author,
		body,
		hue = 'random',
		style,
		variant = 'floating',
		timestamp,
	} = props;
	const resolvedHue: (typeof HUE_PALETTE)[number] =
		hue === 'random' ? hashHue(author) : hue;
	const initial = author.charAt(0).toUpperCase() || '?';

	return (
		<div
			class={`m-bubble m-bubble--${variant}`}
			style={style as Record<string, string> | undefined}
			role="note"
		>
			<span
				class={`m-bubble__avatar m-bubble__avatar--${resolvedHue}`}
				aria-hidden="true"
			>
				{initial}
			</span>
			<div class="m-bubble__content">
				<div class="m-bubble__head">
					<span class="m-bubble__author">{author}</span>
					{timestamp && <span class="m-bubble__time">{timestamp}</span>}
				</div>
				<div class="m-bubble__body">{body}</div>
			</div>
			{variant === 'floating' && <span class="m-bubble__caret" aria-hidden="true" />}
		</div>
	);
}

/**
 * Stable name → hue mapping. Hash the author string to a palette
 * index. Keeps the same person the same colour across the page
 * without an explicit palette argument at every call site.
 */
function hashHue(name: string): (typeof HUE_PALETTE)[number] {
	let h = 0;
	for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
	return HUE_PALETTE[Math.abs(h) % HUE_PALETTE.length] ?? 'cyan';
}
